import { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Package, Truck, Clock, ClipboardList, TrendingUp, AlertTriangle, Factory, Banknote } from 'lucide-react';
import { DaywiseStageChart } from '../components/DaywiseStageChart';

const API = '';

const STAGE_COLORS = {
  'Not Started': '#d4d4d8', Cutting: '#3b82f6', Preparation: '#8b5cf6', Closing: '#ec4899',
  Lasting: '#f97316', Pasting: '#eab308', Finishing: '#22c55e', Packing: '#14b8a6', Dispatch: '#09090B', Completed: '#16a34a'
};

export function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const todayStr = new Date().toISOString().slice(0, 10);
  const [dispatchFrom, setDispatchFrom] = useState(todayStr);
  const [dispatchTo, setDispatchTo] = useState(todayStr);
  const [filtering, setFiltering] = useState(false);

  const fetchStats = async (from, to) => {
    try {
      const params = (from && to) ? `?dispatch_from=${from}&dispatch_to=${to}` : '';
      const { data } = await axios.get(`${API}/api/dashboard/stats${params}`, { withCredentials: true });
      setStats(data);
    } catch {}
    finally { setLoading(false); setFiltering(false); }
  };

  useEffect(() => {
    fetchStats(dispatchFrom, dispatchTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyDispatchFilter = () => {
    if (dispatchFrom && dispatchTo && dispatchFrom > dispatchTo) return;
    setFiltering(true);
    fetchStats(dispatchFrom, dispatchTo);
  };

  const resetDispatchFilter = () => {
    setDispatchFrom(todayStr); setDispatchTo(todayStr);
    setFiltering(true);
    fetchStats(todayStr, todayStr);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-zinc-200 w-48"></div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-24 bg-zinc-200"></div>)}
          </div>
        </div>
      </div>
    );
  }

  const k = stats?.kpis || {};
  const ageingData = stats?.ageing_buckets
    ? Object.entries(stats.ageing_buckets).map(([name, value]) => ({ name, value, fill: name.includes('>60') || name.includes('35-60') ? '#dc2626' : name.includes('20-35') ? '#f97316' : '#3b82f6' }))
    : [];

  const statusData = stats?.status_distribution ? [
    { name: 'Completed', value: stats.status_distribution.completed, fill: '#22C55E' },
    { name: 'Pending', value: stats.status_distribution.pending, fill: '#EAB308' },
  ] : [];

  const stageLoss = (stats?.stage_loss || []).map(s => ({
    name: s.name, loss_pct: s.loss_pct, loss: s.loss, input: s.input
  }));

  const jcTracking = (stats?.jc_tracking || []).map(j => ({
    name: j.job_card, planned: j.planned, actual: j.actual
  }));

  return (
    <div className="p-6" data-testid="dashboard-page">
      <div className="mb-6">
        <h1 className="font-heading font-black text-3xl tracking-tighter text-[#09090B]">Dashboard</h1>
        <p className="text-sm text-zinc-500">Real-time overview of POs, Dispatches, Production & Wages</p>
      </div>

      {/* Dispatch date filter */}
      <div className="mb-3 flex flex-wrap items-end gap-2 text-xs" data-testid="dispatch-filter-bar">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Dispatch From</div>
          <input type="date" value={dispatchFrom} onChange={(e) => setDispatchFrom(e.target.value)} className="h-8 px-2 border border-zinc-300 rounded-sm" data-testid="dispatch-from-input" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Dispatch To</div>
          <input type="date" value={dispatchTo} onChange={(e) => setDispatchTo(e.target.value)} className="h-8 px-2 border border-zinc-300 rounded-sm" data-testid="dispatch-to-input" />
        </div>
        <button onClick={applyDispatchFilter} disabled={filtering} className="h-8 px-3 bg-[#09090B] text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50" data-testid="apply-dispatch-filter">{filtering ? 'Applying…' : 'Apply'}</button>
        <button onClick={resetDispatchFilter} className="h-8 px-3 border border-zinc-300 rounded-sm hover:bg-zinc-50" data-testid="reset-dispatch-filter">Today</button>
        <a
          href="/api/reports/aged-pending-balance?min_days=50"
          download
          className="ml-auto h-8 inline-flex items-center px-3 border border-red-300 bg-red-50 text-red-700 rounded-sm hover:bg-red-100 no-underline text-xs font-medium"
          data-testid="aged-balance-download"
          title="Download balance qty for POs older than 50 days"
        >
          Download Aged Balance (&gt; 50d)
        </a>
        <a
          href="/api/reports/near-complete-pos?threshold_pct=5"
          download
          className="h-8 inline-flex items-center px-3 border border-emerald-300 bg-emerald-50 text-emerald-700 rounded-sm hover:bg-emerald-100 no-underline text-xs font-medium"
          data-testid="near-complete-download"
          title="POs with less than 5% balance remaining"
        >
          Download Near-Complete POs (&lt; 5%)
        </a>
        <a
          href="/api/reports/stuck-skus?min_days=50&min_balance_pct=90"
          download
          className="h-8 inline-flex items-center px-3 border border-orange-300 bg-orange-50 text-orange-700 rounded-sm hover:bg-orange-100 no-underline text-xs font-medium"
          data-testid="stuck-skus-download"
          title="SKUs with >90% balance left on POs older than 50 days"
        >
          Download Stuck SKUs (&gt; 50d, &gt; 90% left)
        </a>
      </div>

      {/* KPI Cards — Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <KPI icon={Package} label="Open POs" value={k.open_pos || 0} />
        <KPI icon={Clock} label="Total Balance Qty" value={k.total_balance?.toLocaleString() || 0} color="text-red-600" />
        <KPI
          icon={Truck}
          label={k.dispatch_range_from && k.dispatch_range_from === k.dispatch_range_to
            ? (k.dispatch_range_from === todayStr ? "Today's Dispatched" : `Dispatched on ${k.dispatch_range_from}`)
            : `Dispatched ${k.dispatch_range_from} → ${k.dispatch_range_to}`}
          value={k.today_dispatched?.toLocaleString() || 0}
          color="text-emerald-600"
        />
        <KPI icon={ClipboardList} label="Total Job Cards" value={k.total_job_cards || 0} sub={`${k.open_job_cards || 0} open`} />
      </div>

      {/* KPI Cards — Row 2 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPI icon={Package} label="Total Ordered" value={k.total_ordered?.toLocaleString() || 0} />
        <KPI icon={Truck} label="Total Dispatched" value={k.total_dispatched?.toLocaleString() || 0} />
        <KPI icon={TrendingUp} label="Fill Rate" value={`${k.fill_rate || 0}%`} />
        <KPI icon={Banknote} label="Wages This Week" value={`${(stats?.wages_this_week?.net_wages || 0).toLocaleString()}`} sub={stats?.wages_total?.net_wages ? `All time: ${stats.wages_total.net_wages.toLocaleString()}` : ''} />
      </div>

      {/* Row: PO Status + Ageing */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-zinc-200 p-4" data-testid="chart-po-status">
          <h3 className="font-heading font-bold text-base mb-3 text-[#09090B]">PO Status</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value">
                  {statusData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 p-4 lg:col-span-2" data-testid="chart-ageing">
          <h3 className="font-heading font-bold text-base mb-3 text-[#09090B]">Ageing Distribution (POs)</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ageingData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" width={75} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="value" name="POs" radius={[0, 3, 3, 0]}>
                  {ageingData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <DaywiseStageChart />

      {/* Row: Job Card Tracking + Stage-wise Loss */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-zinc-200 p-4" data-testid="chart-jc-tracking">
          <h3 className="font-heading font-bold text-base mb-3 text-[#09090B]">Job Card — Planned vs Actual</h3>
          {jcTracking.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={jcTracking} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 9 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="planned" fill="#09090B" name="Planned" radius={[0, 2, 2, 0]} />
                  <Bar dataKey="actual" fill="#22c55e" name="Actual Output" radius={[0, 2, 2, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyState text="No job card data yet" />}
        </div>

        <div className="bg-white border border-zinc-200 p-4" data-testid="chart-stage-loss">
          <h3 className="font-heading font-bold text-base mb-3 text-[#09090B]">Stage-wise Loss %</h3>
          {stageLoss.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stageLoss} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" unit="%" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Bar dataKey="loss_pct" fill="#dc2626" name="Loss %" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyState text="No production data yet" />}
        </div>
      </div>

      {/* Row: JC Loss Ranking + Buyer Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-zinc-200 p-4" data-testid="table-jc-loss">
          <h3 className="font-heading font-bold text-base mb-3 text-[#09090B]">
            <AlertTriangle size={16} className="inline mr-1 text-red-500" /> Top Loss Job Cards
          </h3>
          {(stats?.jc_loss || []).length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="text-left p-2 text-[10px] uppercase text-zinc-500">Job Card</th>
                    <th className="text-left p-2 text-[10px] uppercase text-zinc-500">PO</th>
                    <th className="text-right p-2 text-[10px] uppercase text-zinc-500">Input</th>
                    <th className="text-right p-2 text-[10px] uppercase text-zinc-500">Loss</th>
                    <th className="text-right p-2 text-[10px] uppercase text-zinc-500">Loss %</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats?.jc_loss || []).map((j, i) => (
                    <tr key={i} className="border-t border-zinc-100">
                      <td className="p-2 font-mono font-bold">{j.job_card}</td>
                      <td className="p-2 font-mono">{j.po}</td>
                      <td className="p-2 text-right font-mono">{j.input}</td>
                      <td className="p-2 text-right font-mono text-red-600 font-bold">-{j.loss}</td>
                      <td className="p-2 text-right font-mono text-red-600 font-bold">{j.loss_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState text="No losses recorded yet" />}
        </div>

        <div className="bg-white border border-zinc-200 p-4" data-testid="chart-buyer">
          <h3 className="font-heading font-bold text-base mb-3 text-[#09090B]">Buyer Performance</h3>
          {(stats?.buyer_performance || []).length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.buyer_performance || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" width={90} tick={{ fontSize: 9 }} />
                  <Tooltip /><Legend />
                  <Bar dataKey="ordered" fill="#09090B" name="Ordered" radius={[0, 2, 2, 0]} />
                  <Bar dataKey="dispatched" fill="#22C55E" name="Dispatched" radius={[0, 2, 2, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyState text="No buyer data" />}
        </div>
      </div>

      {/* PO Stage Status */}
      <div className="bg-white border border-zinc-200 p-4" data-testid="table-po-stage">
        <div className="flex items-center gap-2 mb-3">
          <Factory size={16} className="text-zinc-500" />
          <h3 className="font-heading font-bold text-base text-[#09090B]">PO Stage Status</h3>
        </div>
        {(stats?.po_stage_status || []).length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="text-left p-2 text-[10px] uppercase text-zinc-500">PO Number</th>
                  <th className="text-left p-2 text-[10px] uppercase text-zinc-500">Buyer</th>
                  <th className="text-left p-2 text-[10px] uppercase text-zinc-500">Current Stage</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.po_stage_status || []).map((p, i) => (
                  <tr key={i} className="border-t border-zinc-100">
                    <td className="p-2 font-mono font-bold">{p.po}</td>
                    <td className="p-2">{p.buyer}</td>
                    <td className="p-2">
                      <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: (STAGE_COLORS[p.stage] || '#d4d4d8') + '20', color: STAGE_COLORS[p.stage] || '#71717a' }}>
                        {p.stage}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <EmptyState text="No active POs" />}
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color, sub }) {
  return (
    <div className="bg-white border border-zinc-200 p-4" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-1">
        <Icon size={18} className="text-zinc-400" />
      </div>
      <div className={`text-2xl font-black font-mono tracking-tight ${color || 'text-[#09090B]'}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">{label}</div>
      {sub && <div className="text-[10px] text-zinc-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="h-32 flex items-center justify-center text-zinc-400 text-sm">{text}</div>;
}
